import { isMeasurableProduct } from 'common/components/measurement-hint/utils';
import { getPathname } from 'common/selectors/location';
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { patchUrlByLocale } from 'utils';
import { addTrailingSlash } from 'utils/url';
import { getLoyaltyRewards } from 'features/products/ducks/selectors';
import { ProductType } from '../types';

type UseProductCardProps = {
  product: ProductType;
  onCartAdd: (id: string) => void;
  onCartRemove: (id: string) => void;
  onQtyUpdate: (id: string, qty: number) => void;
};

export const useProductCard = ({ product, onCartAdd, onCartRemove, onQtyUpdate }: UseProductCardProps) => {
  const pathname = useSelector(getPathname);
  const loyaltyRewards = useSelector(getLoyaltyRewards);
  const CLOSE_EVENT = 'closeMeasurementTips';
  const productLink = addTrailingSlash(product.productLink);
  const link = patchUrlByLocale(productLink, pathname);
  const reviewsLink = `${link}#reviews`;
  const hasOldPrice = !!product.price?.previous_price;
  const isMeasurable = isMeasurableProduct(product.measurementData);
  const [showMeasurementTip, setShowMeasurementTip] = useState<boolean>(false);
  const hideTip = () => setShowMeasurementTip(false);
  const loyaltyRewardLabel = loyaltyRewards[product.productId]?.label;

  const fireGlobalCloseEvent = useCallback(() => {
    document.dispatchEvent(new CustomEvent(CLOSE_EVENT));
  }, []);

  const handleAdd = useCallback(() => {
    onCartAdd(product.productId);
    fireGlobalCloseEvent();
    if (isMeasurable) {
      setShowMeasurementTip(true);
    }
  }, [fireGlobalCloseEvent, isMeasurable, onCartAdd, product.productId]);

  const handleRemove = useCallback(() => {
    onCartRemove?.(product.productId);
    if (isMeasurable) {
      fireGlobalCloseEvent();
    }
  }, [fireGlobalCloseEvent, isMeasurable, onCartRemove, product.productId]);

  const handleUpdate = useCallback(
    (val: number) => {
      onQtyUpdate?.(product.productId, val);
      if (val > 0 && isMeasurable) {
        fireGlobalCloseEvent();
        setShowMeasurementTip(true);
      }
    },
    [fireGlobalCloseEvent, isMeasurable, onQtyUpdate, product.productId],
  );

  useEffect(() => {
    document.addEventListener(CLOSE_EVENT, hideTip, { passive: true });
    return () => document.removeEventListener(CLOSE_EVENT, hideTip);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', hideTip, { passive: true });
    return () => document.removeEventListener('scroll', hideTip);
  }, []);

  return {
    handleAdd,
    handleRemove,
    handleUpdate,
    reviewsLink,
    hasOldPrice,
    showMeasurementTip,
    setShowMeasurementTip,
    loyaltyRewardLabel,
    link,
    pathname,
  };
};
